import { Instance, types } from "mobx-state-tree";
import { NumberModel } from "../../mobxDataModels/numberModel";
import { SelectModel } from "../../mobxDataModels/selectModel";
import { StringModel } from "../../mobxDataModels/stringModel";
import { ListOfItemsModel } from "../../mobxDataModels/listOfItemsModel";
import { v4 as uuidv4 } from "uuid";

const CRON_REGEX =
  /^(\*|(\d+(-\d+)?))(\/\d+)?(,(\*|(\d+(-\d+)?))(\/\d+)?)*(\s(\*|(\d+(-\d+)?))(\/\d+)?(,(\*|(\d+(-\d+)?))(\/\d+)?)*){4}$/;
function isValidCron(v: string) {
  return CRON_REGEX.test(v) && v.trim().split(/\s+/).length === 5;
}

function parseMemoryString(memory: string): { memoryValue: string; memoryUnit: string } {
  if (!memory) return { memoryValue: "", memoryUnit: "Mi" };
  for (const unit of ["Mi", "Gi"]) {
    if (memory.endsWith(unit)) {
      return { memoryValue: memory.slice(0, -unit.length), memoryUnit: unit };
    }
  }
  return { memoryValue: memory, memoryUnit: "Other" };
}

function parseCpuString(cpu: string): { cpuValue: string; cpuUnit: string } {
  if (!cpu) return { cpuValue: "", cpuUnit: "Millicores" };
  if (cpu.endsWith("m")) {
    return { cpuValue: cpu.slice(0, -1), cpuUnit: "Millicores" };
  }
  return { cpuValue: cpu, cpuUnit: "Cores" };
}

const ContainerOverrideDraftModel = types
  .model({
    id: types.string,
    name: types.optional(types.string, ""),
    memoryValue: types.optional(types.string, ""),
    memoryUnit: types.optional(types.string, "Mi"),
    cpuValue: types.optional(types.string, ""),
    cpuUnit: types.optional(types.string, "Millicores"),
    image: types.optional(types.string, ""),
    command: types.optional(types.string, ""),
    args: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
  })
  .actions((self) => ({
    setField(field: "name" | "memoryValue" | "memoryUnit" | "cpuValue" | "cpuUnit" | "image" | "command", value: string) {
      self[field] = value;
    },
  }))
  .views((self) => ({
    get memoryString() {
      if (!self.memoryValue) return "";
      if (self.memoryUnit === "Other") return self.memoryValue;
      return self.memoryValue + self.memoryUnit;
    },
    get cpuString() {
      if (!self.cpuValue) return "";
      if (self.cpuUnit === "Cores") return self.cpuValue;
      return self.cpuValue + "m";
    },
    get argsArray() {
      return self.args.items.map((i) => i.firstValue).filter(Boolean);
    },
  }));
export interface ContainerOverrideDraftMobx extends Instance<typeof ContainerOverrideDraftModel> {}

const ScheduleEntryDraftModel = types
  .model({
    id: types.string,
    name: types.optional(types.string, ""),
    schedule: types.optional(types.string, ""),
    containerOverrides: types.optional(types.array(ContainerOverrideDraftModel), []),
  })
  .actions((self) => ({
    setName(v: string) {
      self.name = v;
    },
    setSchedule(v: string) {
      self.schedule = v;
    },
    addOverride() {
      self.containerOverrides.push(ContainerOverrideDraftModel.create({ id: uuidv4() }));
    },
    removeOverride(id: string) {
      const idx = self.containerOverrides.findIndex((o) => o.id === id);
      if (idx !== -1) self.containerOverrides.splice(idx, 1);
    },
  }));
export interface ScheduleEntryDraftMobx extends Instance<typeof ScheduleEntryDraftModel> {}

export const WorkloadDraftJobModel = types
  .model({
    _schedule: types.optional(types.string, "* * * * *"),
    _concurrencyPolicy: types.optional(types.string, "Forbid"),
    _historyLimit: types.optional(types.number, 5),
    _restartPolicy: types.optional(types.string, "Never"),
    _activeDeadlineSeconds: types.maybe(types.number),
    _scheduleMode: types.optional(types.string, "single"),
    _schedules: types.optional(types.frozen<any[]>(), []),

    schedule: types.optional(StringModel, () =>
      StringModel.create({ label: "Schedule", isRequired: true, validationKey: "jobSchedule" }),
    ),
    concurrencyPolicy: types.optional(SelectModel, () =>
      SelectModel.create({
        label: "Concurrency Policy",
        initialValue: "Forbid",
        options: [
          { label: "Forbid", value: "Forbid" },
          { label: "Replace", value: "Replace" },
          { label: "Allow", value: "Allow" },
        ],
      }),
    ),
    historyLimit: types.optional(StringModel, () => NumberModel.create({ label: "History Limit", min: 1, max: 10 })),
    restartPolicy: types.optional(SelectModel, () =>
      SelectModel.create({
        label: "Restart Policy",
        initialValue: "Never",
        options: [
          { label: "Never", value: "Never" },
          { label: "On Failure", value: "OnFailure" },
        ],
      }),
    ),
    activeDeadlineSeconds: types.optional(StringModel, () =>
      NumberModel.create({ label: "Active Deadline Seconds", min: 1 }),
    ),
    scheduleMode: types.optional(types.string, "single"),
    schedules: types.optional(types.array(ScheduleEntryDraftModel), []),
  })
  .actions((self) => ({
    reset() {
      self.schedule.setInitialValue(self._schedule);
      self.concurrencyPolicy.setInitialValue(self._concurrencyPolicy);
      self.historyLimit.setInitialValue(String(self._historyLimit));
      self.restartPolicy.setInitialValue(self._restartPolicy);
      self.activeDeadlineSeconds.setInitialValue(
        self._activeDeadlineSeconds === undefined ? "" : String(self._activeDeadlineSeconds),
      );
      self.scheduleMode = self._scheduleMode;
      self.schedules.replace(
        self._schedules.map((s: any) =>
          ScheduleEntryDraftModel.create({
            id: uuidv4(),
            name: s.name,
            schedule: s.schedule,
            containerOverrides: (s.containerOverrides || []).map((co: any) => {
              const mem = parseMemoryString(co.memory || "");
              const cpu = parseCpuString(co.cpu || "");
              return ContainerOverrideDraftModel.create({
                id: uuidv4(),
                name: co.name || "",
                memoryValue: mem.memoryValue,
                memoryUnit: mem.memoryUnit,
                cpuValue: cpu.cpuValue,
                cpuUnit: cpu.cpuUnit,
                image: co.image || "",
                command: co.command || "",
                args: {
                  _items: (co.args || []).map((a: string) => ({ firstValue: a })),
                },
              });
            }),
          }),
        ),
      );
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
    confirm() {
      self._schedule = self.schedule.value;
      self.schedule.confirm();
      self._concurrencyPolicy = self.concurrencyPolicy.value;
      self.concurrencyPolicy.confirm();
      self._historyLimit = Number(self.historyLimit.value);
      self.historyLimit.confirm();
      self._restartPolicy = self.restartPolicy.value;
      self.restartPolicy.confirm();
      self._activeDeadlineSeconds =
        self.activeDeadlineSeconds.value.length > 0 ? Number(self.activeDeadlineSeconds.value) : undefined;
      self.activeDeadlineSeconds.confirm();
      self._scheduleMode = self.scheduleMode;
      self._schedules = self.schedules.map((e) => ({
        name: e.name,
        schedule: e.schedule,
        containerOverrides: e.containerOverrides
          .filter((co) => co.name)
          .map((co) => ({
            name: co.name,
            ...(co.memoryString ? { memory: co.memoryString } : {}),
            ...(co.cpuString ? { cpu: co.cpuString } : {}),
            ...(co.image ? { image: co.image } : {}),
            ...(co.command ? { command: co.command } : {}),
            ...(co.argsArray.length > 0 ? { args: co.argsArray } : {}),
          })),
      }));
    },
    setScheduleMode(mode: "single" | "multiple") {
      self.scheduleMode = mode;
      if (mode === "multiple" && self.schedules.length === 0) {
        self.schedules.push(ScheduleEntryDraftModel.create({ id: uuidv4() }));
      }
    },
    addScheduleEntry() {
      self.schedules.push(ScheduleEntryDraftModel.create({ id: uuidv4() }));
    },
    removeScheduleEntry(id: string) {
      const idx = self.schedules.findIndex((e) => e.id === id);
      if (idx !== -1) self.schedules.splice(idx, 1);
    },
  }))
  .views((self) => ({
    get isDirty() {
      let res = false;
      if (self.scheduleMode !== self._scheduleMode) res = true;
      if (self.scheduleMode === "multiple") {
        const current = JSON.stringify(
          self.schedules.map((e) => ({
            name: e.name,
            schedule: e.schedule,
            containerOverrides: e.containerOverrides.map((co) => ({
              name: co.name,
              memoryValue: co.memoryValue,
              memoryUnit: co.memoryUnit,
              cpuValue: co.cpuValue,
              cpuUnit: co.cpuUnit,
              image: co.image,
              command: co.command,
              args: co.args.items.map((i) => i.firstValue),
            })),
          })),
        );
        const confirmed = JSON.stringify(
          self._schedules.map((s: any) => ({
            name: s.name,
            schedule: s.schedule,
            containerOverrides: (s.containerOverrides || []).map((co: any) => {
              const mem = parseMemoryString(co.memory || "");
              const cpu = parseCpuString(co.cpu || "");
              return {
                name: co.name || "",
                memoryValue: mem.memoryValue,
                memoryUnit: mem.memoryUnit,
                cpuValue: cpu.cpuValue,
                cpuUnit: cpu.cpuUnit,
                image: co.image || "",
                command: co.command || "",
                args: co.args || [],
              };
            }),
          })),
        );
        if (current !== confirmed) res = true;
      } else {
        if (self.schedule.isDirty) res = true;
      }
      if (self.concurrencyPolicy.isDirty) res = true;
      if (self.historyLimit.isDirty) res = true;
      if (self.restartPolicy.isDirty) res = true;
      if (self.activeDeadlineSeconds.isDirty) res = true;
      return res;
    },
    get isValid() {
      let res = true;
      if (self.scheduleMode === "multiple") {
        if (self.schedules.length === 0) res = false;
        for (const entry of self.schedules) {
          if (!entry.name) res = false;
          if (!entry.schedule || !isValidCron(entry.schedule)) res = false;
        }
      } else {
        if (!self.schedule.isValid) res = false;
      }
      if (!self.historyLimit.isValid) res = false;
      if (!self.activeDeadlineSeconds.isValid) res = false;
      return res;
    },
    get asObject() {
      let res: any = {
        concurrencyPolicy: self.concurrencyPolicy.value,
        historyLimit: Number(self.historyLimit.value),
        restartPolicy: self.restartPolicy.value,
      };
      if (self.scheduleMode === "multiple") {
        res.schedules = self.schedules.map((e) => {
          const entry: any = { name: e.name, schedule: e.schedule };
          const overrides = e.containerOverrides
            .filter((co) => co.name)
            .map((co) => {
              const o: any = { name: co.name };
              if (co.memoryString) o.memory = co.memoryString;
              if (co.cpuString) o.cpu = co.cpuString;
              if (co.image) o.image = co.image;
              if (co.command) o.command = co.command;
              if (co.argsArray.length > 0) o.args = co.argsArray;
              return o;
            });
          if (overrides.length) entry.containerOverrides = overrides;
          return entry;
        });
      } else {
        res.schedule = self.schedule.value;
      }
      if (self.activeDeadlineSeconds.value.length > 0) {
        res.activeDeadlineSeconds = Number(self.activeDeadlineSeconds.value);
      }
      return res;
    },
  }))
  .views((self) => ({
    get isDirtyReason() {
      if (!self.isDirty) {
        return "WorkloadDraftJob - Not Dirty";
      }
      let reason = "";
      if (self.scheduleMode !== self._scheduleMode) reason = "scheduleMode changed";
      if (self.scheduleMode === "multiple") {
        reason = "schedules changed";
      } else {
        if (self.schedule.isDirty) reason = self.schedule.isDirtyReason;
      }
      if (self.concurrencyPolicy.isDirty) reason = self.concurrencyPolicy.isDirtyReason;
      if (self.historyLimit.isDirty) reason = self.historyLimit.isDirtyReason;
      if (self.restartPolicy.isDirty) reason = self.restartPolicy.isDirtyReason;
      if (self.activeDeadlineSeconds.isDirty) reason = self.activeDeadlineSeconds.isDirtyReason;
      return "WorkloadDraftJob - " + reason;
    },
  }));
export interface WorkloadDraftJobMobx extends Instance<typeof WorkloadDraftJobModel> {}
